import React, { useEffect, useState } from 'react';
import { Loader2, Download, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

/**
 * PdfCoverDialog — collects Teacher Name + Date for the auto-generated cover
 * page that gets prepended to any downloaded PDF.
 *
 * The dialog defaults teacher_name to the logged-in user's display name and
 * date to today. Both fields are editable.
 *
 * Props:
 *   open          controlled open state
 *   onOpenChange  (bool) → void
 *   onConfirm     ({ teacher_name, date }) → Promise|void
 *   downloading   bool — shows a spinner in the confirm button
 *   context       { title, resource_label, grade, unit_number, subtitle }
 */
export default function PdfCoverDialog({
  open,
  onOpenChange,
  onConfirm,
  downloading,
  context = {},
}) {
  const { user } = useAuth();
  const defaultName = user?.preferences?.default_teacher_name || user?.name || '';
  const defaultDate = React.useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }, []);

  const [teacherName, setTeacherName] = useState(defaultName);
  const [date, setDate] = useState(defaultDate);

  // Reset the fields when the dialog opens fresh (or user changes)
  useEffect(() => {
    if (open) {
      setTeacherName(defaultName);
      setDate(defaultDate);
    }
  }, [open, defaultName, defaultDate]);

  const handleConfirm = async () => {
    await onConfirm({
      teacher_name: teacherName.trim(),
      date: date.trim(),
    });
  };

  const contextLine = [
    context.grade && `Grade ${context.grade}`,
    context.unit_number != null && `Unit ${context.unit_number}`,
  ].filter(Boolean).join(' · ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md bg-[#FDFBF7] border border-[#EBE7E0]"
        data-testid="pdf-cover-dialog"
      >
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#D4AF37] font-semibold mb-1">
            Cover Page
          </div>
          <DialogTitle className="font-serif-display text-xl text-[#14213D]">
            Personalize your download
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5A6577]">
            We&rsquo;ll add a branded Atlas History cover as page 1 of your PDF.
          </DialogDescription>
        </DialogHeader>

        {/* Context preview */}
        {(context.title || context.resource_label) && (
          <div className="rounded-md border border-[#EBE7E0] bg-white/60 px-3 py-2">
            {context.resource_label && (
              <div className="text-[9px] uppercase tracking-[0.24em] text-[#D4AF37] font-semibold">
                {context.resource_label}
              </div>
            )}
            {context.title && (
              <div className="font-serif text-[#14213D] text-base mt-0.5 leading-snug">
                {context.title}
              </div>
            )}
            {contextLine && (
              <div className="text-xs text-[#5A6577] mt-1">{contextLine}</div>
            )}
          </div>
        )}

        <div className="space-y-3 pt-1">
          <div>
            <Label htmlFor="cover-teacher-name" className="text-xs uppercase tracking-wider text-[#14213D]">
              Teacher Name
            </Label>
            <Input
              id="cover-teacher-name"
              value={teacherName}
              onChange={(e) => setTeacherName(e.target.value)}
              placeholder="e.g., Mr. Casteel"
              className="mt-1 bg-white"
              data-testid="cover-teacher-name-input"
              autoFocus
            />
          </div>
          <div>
            <Label htmlFor="cover-date" className="text-xs uppercase tracking-wider text-[#14213D]">
              Date
            </Label>
            <Input
              id="cover-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="e.g., February 12, 2026"
              className="mt-1 bg-white"
              data-testid="cover-date-input"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={downloading}
            data-testid="cover-cancel-btn"
          >
            <X className="h-4 w-4 mr-1.5" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={downloading}
            className="bg-[#14213D] text-[#FDFBF7] hover:bg-[#0f172a]"
            data-testid="cover-download-btn"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Generating&hellip;
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-1.5" />
                Download PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
